import React, { useEffect, useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import OfferCard from "../../Offers/OfferCard/OfferCard";
import MainButton from "../../Elements/MainButton/MainButton";
import SecondButton from "../../Elements/SecondButton/SecondButton";
import { fetchTopUpPackages } from "../../../services/TopUpService";
import { useCart } from "../../../context/CartContext";
import { AuthContext } from "../../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import AddedToCartModal from "../../Offers/AddedToCartModal/AddedToCartModal";
import "../../Offers/OffersList/OffersList.css";
import "./TopUpPackagesSection.css";

const TopUpPackagesSection = ({ iccid, esimDetails }) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || "en";

  const { token, walletCurrency } = useContext(AuthContext);
  const { addItems, items: cartItems, removeItem } = useCart();
  const navigate = useNavigate();

  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedPackageId, setSelectedPackageId] = useState(null);
  const [isAddedModalOpen, setIsAddedModalOpen] = useState(false);

  useEffect(() => {
    if (iccid && token) {
      setLoading(true);
      setError("");
      setPackages([]);

      fetchTopUpPackages({ iccid, token, currency: walletCurrency || "EUR" })
        .then((data) => {
          const mapped = data.map((item) => ({
            id: item.package_id,
            internal_package_id: item.package_id,
            package_code: item.package_code,
            target_iccid: iccid,
            name: `${t("esim.topUpPackages.topUp", "Top-Up")} ${
              item.data_limit >= 1024 ? (item.data_limit / 1024).toFixed(0) + "GB" : item.data_limit + "MB"
            }`,
            gigabytes: item.data_limit / 1024,
            duration: item.duration,
            price: item.amount,
            currency: item.currency,
            top_up_available: true,
            type: "topup",
            country_id: item.country_id,
            region_id: item.region_id,
          }));

          setPackages(mapped);
          setLoading(false);
        })
        .catch((err) => {
          console.error(err);
          setError(t("esim.topUpPackages.failedToLoad", "Failed to load top-up packages."));
          setLoading(false);
        });
    }
  }, [iccid, token, walletCurrency, t]);

  const selectedPackage = packages.find((p) => p.id === selectedPackageId);

  const handleAddToCart = () => {
    if (!selectedPackage) return false;

    if (cartItems.length > 0) {
      alert(t("esim.topUpPackages.limitAlert", "Only 1 top-up can be purchased at a time."));
      return false;
    }

    const itemToAdd = {
      ...selectedPackage,
      is_topup: true,
      iccid,
      quantity: 1,
    };

    addItems([itemToAdd]);
    setIsAddedModalOpen(true);
    return true;
  };

  const handleBuyNow = () => {
    const success = handleAddToCart();
    if (success) {
      navigate(`/${currentLang}/cart`);
    }
  };

  return (
    <div className="topup-section-container">
      <div className="topup-header">
        <h3>{t("esim.topUpPackages.headerTitle", "Add Data to Your Plan")}</h3>
        <p className="topup-subtitle">{t("esim.topUpPackages.headerSubtitle", "Select a package to top up eSIM")}</p>
      </div>

      {loading && <p className="text-muted">{t("esim.topUpPackages.loadingOptions", "Loading options...")}</p>}
      {error && <p className="error-text">{error}</p>}

      {!loading && !error && packages.length > 0 && (
        <>
          <div className="offers-grid-wrapper topup-grid-wrapper">
            <div className="offers-grid">
              {packages.map((pkg) => (
                <OfferCard
                  key={pkg.id}
                  id={pkg.id}
                  gigabytes={pkg.gigabytes}
                  duration={pkg.duration}
                  price={pkg.price}
                  currency={pkg.currency}
                  selected={selectedPackageId === pkg.id}
                  onToggle={() => setSelectedPackageId((prev) => (prev === pkg.id ? null : pkg.id))}
                  topUpAvailable={true}
                />
              ))}
            </div>
          </div>

          <div className="offers-actions-bar topup-actions-bar">
            <SecondButton onClick={handleAddToCart} disabled={!selectedPackage}>
              {t("esim.topUpPackages.addToCart", "Add to cart")}
            </SecondButton>
            <MainButton onClick={handleBuyNow} disabled={!selectedPackage}>
              {t("esim.topUpPackages.buyNow", "Buy now")}
            </MainButton>
          </div>
        </>
      )}

      <AddedToCartModal
        isOpen={isAddedModalOpen}
        onClose={() => setIsAddedModalOpen(false)}
        onGoToCart={() => navigate(`/${currentLang}/cart`)}
        onRemoveItem={removeItem}
        items={cartItems}
        currency={walletCurrency || "EUR"}
      />
    </div>
  );
};

export default TopUpPackagesSection;
